import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuth } from '../context/AuthContext';
import { WO_TYPES, WO_TYPE_LABELS, WO_TYPE_COLORS, STATUSES, PRIORITIES, FREQUENCIES } from '../utils/constants';
import { FiX, FiUpload, FiTrash2, FiSearch, FiPlus, FiLayers } from 'react-icons/fi';
import UploadProgress from './UploadProgress';
import titleCase from '../utils/titleCase';
import { playSuccess, playError } from '../utils/sounds';
import { useTheme } from '../context/ThemeContext';


export default function WorkOrderCreateModal({ onClose, onCreated, preselectedAssetId, preselectedType, parentWorkOrder }) {
  const { user, authFetch, token } = useAuth();
  const { isDark } = useTheme();

  const [form, setForm] = useState({
    type: preselectedType || 'PM01',
    name: '',
    description: '',
    asset_id: preselectedAssetId || '',
    priority: 'Normal',
    assigned_to: '',
    due_date: '',
    frequency: '1 Month',
    status: 'New',
    estimated_hours: '',
    estimated_minutes: ''
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successResult, setSuccessResult] = useState(null);

  // Asset search
  const [assets, setAssets] = useState([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [selectedAssetName, setSelectedAssetName] = useState('');

  // Users list for assignment
  const [users, setUsers] = useState([]);

  // Planned operations - default op 0010
  const opIdRef = useRef(1);
  const [plannedOps, setPlannedOps] = useState([{ _id: 0, title: '', est_hours: 0, est_minutes: 0, planned_date: '', resources: 1, isDefault: true }]);
  const [showAddOp, setShowAddOp] = useState(false);
  const [newOp, setNewOp] = useState({ title: '', est_hours: 0, est_minutes: 0, planned_date: '', resources: 1 });

  // Job load data

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const assetSearchRef = useRef(null);
  const assetDropdownRef = useRef(null);

  // Load users
  useEffect(() => {
    authFetch('/api/users').then(r => r.json()).then(setUsers).catch(() => {});
  }, []);

  // Close asset dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (assetDropdownRef.current && !assetDropdownRef.current.contains(e.target)) {
        setShowAssetDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load assets on search
  useEffect(() => {
    authFetch('/api/assets?limit=200').then(r => r.json()).then(res => {
      const data = res && res.data ? res.data : (Array.isArray(res) ? res : []);
      setAssets(data);
      if (preselectedAssetId) {
        const a = data.find(a => a.id === parseInt(preselectedAssetId));
        if (a) setSelectedAssetName(`${a.tag} - ${a.name}`);
      }
    }).catch(() => {});
  }, []);



  const filteredAssets = assets.filter(a =>
    (a.status_name || '').toLowerCase() !== 'redundant' &&
    (!assetSearch || a.name.toLowerCase().includes(assetSearch.toLowerCase()) ||
    a.tag?.toLowerCase().includes(assetSearch.toLowerCase()))
  );

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).slice(0, 8 - files.length);
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    // Validate all operations have required fields
    for (let i = 0; i < plannedOps.length; i++) {
      const op = plannedOps[i];
      if (!op.title.trim()) { setError(`Operation ${String((i + 1) * 10).padStart(4, '0')} requires a name`); return; }
      if (!op.planned_date) { setError(`Operation ${String((i + 1) * 10).padStart(4, '0')} requires a requested date`); return; }
    }

    setSaving(true);
    setError('');
    setUploadProgress(0);

    try {
      const firstDate = plannedOps[0]?.planned_date;
      const dayOfWeek = new Date().getDay() || 7;
      const monday = new Date(); monday.setDate(monday.getDate() - dayOfWeek + 1); monday.setHours(0,0,0,0);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      const sundayStr = sunday.toISOString().split('T')[0];
      const autoType = firstDate && firstDate <= sundayStr ? 'PM03' : 'PM01';
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('description', form.description);
      formData.append('asset_id', form.asset_id);
      formData.append('type', autoType);
      formData.append('priority', form.priority);
      formData.append('status', autoType === 'PM03' ? 'In Progress' : 'New');
      formData.append('due_date', form.due_date);
      formData.append('assigned_to', form.assigned_to || user.id);
      const estHours = parseFloat(form.estimated_hours) || 0;
      const estMinutes = parseInt(form.estimated_minutes) || 0;
      const totalEstimated = estHours + (estMinutes / 60);
      if (totalEstimated > 0) formData.append('estimated_hours', totalEstimated);
      if (parentWorkOrder) formData.append('parent_wo_id', parentWorkOrder.id);
      if (plannedOps.length > 0) {
        const serializedOps = plannedOps.map(op => ({
          title: op.title.trim(),
          estimated_hours: (op.est_hours || 0) + (op.est_minutes || 0) / 60,
          planned_date: op.planned_date || null,
          resources: op.resources || 1
        }));
        formData.append('planned_operations', JSON.stringify(serializedOps));
      }
      files.forEach(f => formData.append('files', f));

      // Use XMLHttpRequest for upload progress
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/notifs');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try {
              reject(new Error(JSON.parse(xhr.responseText).error));
            } catch {
              reject(new Error('Failed to create notification'));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      playSuccess();
      setSuccessResult(result);
      onCreated && onCreated(result);
    } catch (err) {
      setError(err.message);
      playError();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        style={{
          background: 'var(--bg-card)', borderRadius: 16, width: '95%', maxWidth: 560,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '20px 24px', borderBottom: '1px solid var(--border)',
          background: 'var(--bg-card)', flexShrink: 0, borderRadius: '16px 16px 0 0'
        }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: parentWorkOrder ? 'rgba(99,102,241,0.1)' : 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {parentWorkOrder ? <FiLayers size={20} color="#6366f1" /> : <FiPlus size={20} color="#2563eb" />}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{parentWorkOrder ? 'New Sub Notification' : 'New Notification'}</h2>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>{parentWorkOrder ? 'Create a linked notification under the parent work order' : 'Submit a work request for supervisor approval'}</p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)'
          }}><FiX size={20} /></button>
        </div>

        {successResult ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{parentWorkOrder ? 'Sub Notification Created' : 'Notification Created'}</h3>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#2563eb', marginBottom: 12, fontFamily: 'monospace', letterSpacing: 1 }}>
              {successResult.notif_number}
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
              Has been sent to supervisor for review and approval.
            </p>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={onClose}
              style={{ padding: '12px 32px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
              Done
            </motion.button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
          <div style={{ padding: 24, overflow: 'auto', flex: 1 }}>
          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', color: '#ef4444',
              fontSize: 13, marginBottom: 16, border: '1px solid rgba(220,38,38,0.2)'
            }}>{error}</div>
          )}

          {/* Parent WO indicator */}
          {parentWorkOrder && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, background: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.2)', marginBottom: 16, fontSize: 13, color: '#6366f1'
            }}>
              <span style={{ fontWeight: 600 }}>Sub notification of:</span> {parentWorkOrder.wo_number} — {parentWorkOrder.name}
            </div>
          )}

          {/* Auto-determined type indicator - only show when a date is selected */}
          {(() => {
            const firstDate = plannedOps[0]?.planned_date;
            if (!firstDate) return null;
            const dayOfWeek = new Date().getDay() || 7;
            const monday = new Date(); monday.setDate(monday.getDate() - dayOfWeek + 1); monday.setHours(0,0,0,0);
            const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
            const sundayStr = sunday.toISOString().split('T')[0];
            const autoType = firstDate <= sundayStr ? 'PM03' : 'PM01';
            const typeInfo = autoType === 'PM03'
              ? { label: 'Breakdown', desc: 'Within current working week', color: '#f59e0b' }
              : { label: 'Planned', desc: 'Scheduled after this week', color: '#2563eb' };
            return (
              <div style={{ padding: '8px 14px', borderRadius: 8, background: `${typeInfo.color}10`, border: `1px solid ${typeInfo.color}30`, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: typeInfo.color }}>{autoType}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{typeInfo.label} — {typeInfo.desc}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>Based on requested date</div>
              </div>
            );
          })()}

          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Title <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="text" value={form.name}
              onChange={e => { const v = titleCase(e.target.value); setForm(f => ({ ...f, name: v })); setPlannedOps(prev => prev.map((o, i) => i === 0 && o.isDefault ? { ...o, title: v } : o)); }}
              placeholder="Work order title"
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
                fontSize: 14, outline: 'none', boxSizing: 'border-box',
                background: 'var(--bg-input)', color: 'var(--text-primary)', transition: 'border-color 0.2s'
              }}
              onFocus={e => e.target.style.borderColor = '#2563eb'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>

          {/* Asset Search */}
          <div ref={assetDropdownRef} style={{ marginBottom: 16, position: 'relative' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Asset</label>
            <div style={{ position: 'relative' }}>
              <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="text" ref={assetSearchRef}
                value={form.asset_id ? selectedAssetName : assetSearch}
                onChange={e => {
                  const v = e.target.value.replace(/(^|\s)([a-z])/g, (m, p, l) => p + l.toUpperCase());
                  setAssetSearch(v);
                  setForm(f => ({ ...f, asset_id: '' }));
                  setSelectedAssetName('');
                  setShowAssetDropdown(true);
                }}
                onFocus={() => setShowAssetDropdown(true)}
                placeholder="Search assets..."
                style={{
                  width: '100%', padding: '10px 12px 10px 32px', borderRadius: 8, border: '1px solid var(--border)',
                  fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  background: 'var(--bg-input)', color: 'var(--text-primary)', transition: 'border-color 0.2s'
                }}
                onFocusCapture={e => e.target.style.borderColor = '#2563eb'}
                onBlurCapture={e => e.target.style.borderColor = 'var(--border)'}
              />
              {form.asset_id && (
                <button type="button" onClick={() => { setForm(f => ({ ...f, asset_id: '' })); setSelectedAssetName(''); setAssetSearch(''); }}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
                ><FiX size={14} /></button>
              )}
            </div>
            {showAssetDropdown && !form.asset_id && (
              <div style={{
                position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 10,
                background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 200,
                overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: 4
              }}>
                {filteredAssets.slice(0, 20).map(a => (
                  <div key={a.id}
                    onClick={() => {
                      setForm(f => ({ ...f, asset_id: a.id }));
                      setSelectedAssetName(`${a.tag} - ${a.name}`);
                      setShowAssetDropdown(false);
                    }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                      borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.1s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border-subtle)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
                  >
                    <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{a.tag}</span>
                    <span style={{ color: 'var(--text-secondary)' }}> - {a.name}</span>
                  </div>
                ))}
                {filteredAssets.length === 0 && (
                  <div style={{ padding: '12px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No assets found</div>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Description</label>
            <textarea value={form.description}
              onChange={e => {
                let v = e.target.value;
                if (v.length > 0) v = v.charAt(0).toUpperCase() + v.slice(1);
                v = v.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                setForm(f => ({ ...f, description: v }));
              }}
              placeholder="Describe the work to be done..."
              rows={3}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
                fontSize: 13, lineHeight: 1.5, outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'monospace',
                background: 'var(--bg-input)', color: 'var(--text-primary)', transition: 'border-color 0.2s'
              }}
              onFocus={e => e.target.style.borderColor = '#2563eb'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>



          {/* Planned Operations */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
              <FiLayers size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Operations {plannedOps.length > 0 && `(${plannedOps.length})`}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
              {plannedOps.map((op, idx) => (
                <div key={op._id} style={{ padding: 14, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
                      {String((idx + 1) * 10).padStart(4, '0')}
                    </span>
                    {!op.isDefault && (
                      <button type="button" onClick={() => setPlannedOps(prev => prev.filter((_, i) => i !== idx))}
                        style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: '#dc2626', fontSize: 10 }}>
                        <FiTrash2 size={12} />
                      </button>
                    )}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Operation Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <input value={op.title} onChange={e => { const v = titleCase(e.target.value); setPlannedOps(prev => prev.map((o, i) => i === idx ? { ...o, title: v, isDefault: false } : o)); }}
                      placeholder="Operation name..."
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Estimated Time <span style={{ color: '#ef4444' }}>*</span></label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <select value={op.est_hours} onChange={e => setPlannedOps(prev => prev.map((o, i) => i === idx ? { ...o, est_hours: parseInt(e.target.value) || 0 } : o))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                          {Array.from({ length: 13 }, (_, i) => i).map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: 'center' }}>Hours</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <select value={op.est_minutes} onChange={e => setPlannedOps(prev => prev.map((o, i) => i === idx ? { ...o, est_minutes: parseInt(e.target.value) || 0 } : o))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                          <option value="0">0</option><option value="15">15</option><option value="30">30</option><option value="45">45</option>
                        </select>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: 'center' }}>Minutes</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Requested Date <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="date" value={op.planned_date} onChange={e => setPlannedOps(prev => prev.map((o, i) => i === idx ? { ...o, planned_date: e.target.value } : o))} min={new Date().toISOString().split('T')[0]}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {showAddOp ? (
              <div style={{ padding: 14, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Operation Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input value={newOp.title} onChange={e => setNewOp(o => ({ ...o, title: titleCase(e.target.value) }))} autoFocus placeholder="Operation name..."
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Estimated Time <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <select value={newOp.est_hours} onChange={e => setNewOp(o => ({ ...o, est_hours: parseInt(e.target.value) || 0 }))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                        {Array.from({ length: 13 }, (_, i) => i).map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: 'center' }}>Hours</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <select value={newOp.est_minutes} onChange={e => setNewOp(o => ({ ...o, est_minutes: parseInt(e.target.value) || 0 }))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                        <option value="0">0</option><option value="15">15</option><option value="30">30</option><option value="45">45</option>
                      </select>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: 'center' }}>Minutes</div>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Requested Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="date" value={newOp.planned_date} onChange={e => setNewOp(o => ({ ...o, planned_date: e.target.value }))} min={new Date().toISOString().split('T')[0]}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => { setShowAddOp(false); setNewOp({ title: '', est_hours: 0, est_minutes: 0, planned_date: '', resources: 1 }); }}
                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
                    Cancel
                  </button>
                  <button type="button" onClick={() => {
                    if (!newOp.title.trim() || !newOp.planned_date || (newOp.est_hours === 0 && newOp.est_minutes === 0)) return;
                    setPlannedOps(prev => [...prev, { _id: opIdRef.current++, title: newOp.title.trim(), est_hours: newOp.est_hours, est_minutes: newOp.est_minutes, estimated_hours: (newOp.est_hours || 0) + (newOp.est_minutes || 0) / 60, planned_date: newOp.planned_date, resources: 1 }]);
                    setNewOp({ title: '', est_hours: 0, est_minutes: 0, planned_date: '', resources: 1 });
                    setShowAddOp(false);
                  }}
                    disabled={!newOp.title.trim() || !newOp.planned_date || (newOp.est_hours === 0 && newOp.est_minutes === 0)}
                    style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: newOp.title.trim() && newOp.planned_date && (newOp.est_hours > 0 || newOp.est_minutes > 0) ? 1 : 0.5 }}>
                    <FiPlus size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Add Operation
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setShowAddOp(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#2563eb', width: '100%', justifyContent: 'center' }}>
                <FiPlus size={14} /> Add Operation
              </button>
            )}
          </div>

          {/* File Upload */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
              Attachments {files.length > 0 && `(${files.length}/8)`}
            </label>
            <input type="file" ref={fileInputRef} multiple onChange={handleFileChange}
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => fileInputRef.current?.click()}
                disabled={files.length >= 8}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                  borderRadius: 8, border: '1px dashed var(--border)', background: 'var(--bg-input)',
                  cursor: files.length >= 8 ? 'not-allowed' : 'pointer', fontSize: 13,
                  color: 'var(--text-secondary)', justifyContent: 'center'
                }}
              >
                <FiUpload size={14} /> Browse Files
              </button>
            </div>

            {files.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {files.map((file, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                    borderRadius: 6, background: 'var(--border-subtle)', marginTop: 4, fontSize: 13
                  }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                    <button type="button" onClick={() => removeFile(i)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2
                    }}><FiTrash2 size={12} /></button>
                  </div>
                ))}
              </div>
            )}

            {saving && <UploadProgress files={files} progress={uploadProgress} uploading={true} />}
          </div>


          </div>
          {/* Submit - fixed at bottom */}
          {(() => {
            const opsValid = plannedOps.length > 0 && plannedOps.every(op => op.title.trim() && op.planned_date && (op.est_hours > 0 || op.est_minutes > 0));
            const formValid = form.name.trim() && opsValid;
            const canSubmit = formValid && !saving;
            return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg-card)', borderRadius: '0 0 16px 16px' }}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14, fontWeight: 500
            }}>Cancel</button>
            <motion.button type="submit" disabled={!canSubmit}
              whileHover={canSubmit ? { scale: 1.02 } : {}} whileTap={canSubmit ? { scale: 0.98 } : {}}
              style={{
                padding: '10px 28px', borderRadius: 10, border: 'none',
                background: canSubmit ? '#2563eb' : '#94a3b8', color: '#fff',
                cursor: canSubmit ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600,
                opacity: canSubmit ? 1 : 0.5,
                boxShadow: canSubmit ? '0 4px 12px rgba(37,99,235,0.3)' : 'none'
              }}
            >{saving ? (files.length > 0 && uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Submitting…') : 'Submit Notification'}</motion.button>
          </div>
            );
          })()}
        </form>
        )}
      </motion.div>
    </motion.div>
  );
}
